import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiThumbsUp, FiThumbsDown, FiSearch, FiMessageCircle, FiStar, FiClock, FiImage } from 'react-icons/fi';
import { getFeedbackSummary, getBranchMenuItems, MenuItem, FeedbackItem, BranchSummary, ItemSummary } from '../../../../utils/supabaseClient';
import { useBranch } from '../../../../context/BranchContext';
import PaginationWithLimitSelector from '../../../../components/ui/PaginationWithLimitSelector';
import Select from '../../../../components/ui/Select';
import { motion, AnimatePresence } from 'framer-motion';
import i18n from '../../../../i18n/config';

const  FeedbackPage: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useBranch();

    const [branchSummary, setBranchSummary] = useState<BranchSummary | null>(null);
    const [itemsSummary, setItemsSummary] = useState<ItemSummary[] | null>(null);
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [selectedItemId, setSelectedItemId] = useState<string | 'all'>('all');
    const [sentiment, setSentiment] = useState<'all' | 'positive' | 'negative'>('all');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'general' | 'items'>('general');
    const [writtenBy, setWrittenBy] = useState<1 | -1>(1); // 1 for customer, -1 for AI

    // Separate pagination states for each tab
    const [generalPagination, setGeneralPagination] = useState({
        currentPage: 1,
        totalCount: 0,
        itemsPerPage: 10
    });

    const [itemsPagination, setItemsPagination] = useState({
        currentPage: 1,
        totalCount: 0,
        itemsPerPage: 10
    });

    const loadMenuItems = async () => {
        if (!branchId) return;
        try {
            const result = await getBranchMenuItems(branchId, 1, 200);
            if (result.data) setMenuItems(result.data);
        } catch (err) {
            console.error('Failed to load menu items', err);
        }
    };

    const loadFeedbacks = async (page?: number) => {
        if (!branchId) return;
        
        setError(null);
        setIsLoading(true);

        try {
            const payload = {
                branch_id: branchId,
                item_filter: activeTab === 'general' 
                    ? 'no_items' as const
                    : selectedItemId === 'all' 
                        ? 'all_items' as const 
                        : 'item_id' as const,
                item_id: selectedItemId !== 'all' ? selectedItemId : null,
                written_by: writtenBy,
                attribute_filter: {
                    session_id: null,
                    is_positive: sentiment === 'all' ? null : sentiment === 'positive'
                }
            };

            const result = await getFeedbackSummary(payload);
            
            if (!result.success || !result.data) {
                setError(result.message || 'Unable to fetch feedbacks');
                setBranchSummary(null);
                setItemsSummary(null);
            } else {
                if (activeTab === 'general') {
                    setBranchSummary(result.data.branch_summary);
                    setItemsSummary(null);
                    setGeneralPagination(prev => ({
                        ...prev,
                        totalCount: result.data?.branch_summary?.feedbacks.length || 0
                    }));
                } else {
                    setBranchSummary(null);
                    setItemsSummary(result.data.items_summary);
                    const totalItems = result.data?.items_summary?.reduce((sum, item) => sum + item.feedbacks.length, 0) || 0;
                    setItemsPagination(prev => ({
                        ...prev,
                        totalCount: totalItems
                    }));
                }
            }
        } catch (err) {
            console.error('Error fetching feedbacks', err);
            setError('Error fetching feedbacks');
            setBranchSummary(null);
            setItemsSummary(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadMenuItems();
    }, [branchId]);

    useEffect(() => {
        // Load feedbacks when tab changes or filters change
        loadFeedbacks(1); // Always start from page 1 when switching tabs or filters
    }, [activeTab, selectedItemId, sentiment, branchId, writtenBy]);

    useEffect(() => {
        // Reset pagination when items per page changes
        if (activeTab === 'general') {
            setGeneralPagination(prev => ({ ...prev, currentPage: 1 }));
        } else {
            setItemsPagination(prev => ({ ...prev, currentPage: 1 }));
        }
        loadFeedbacks(1);
    }, [generalPagination.itemsPerPage, itemsPagination.itemsPerPage]);

    const generalFeedbacks = useMemo(() => {
        return branchSummary?.feedbacks || [];
    }, [branchSummary]);

    const itemFeedbacks = useMemo(() => {
        return itemsSummary || [];
    }, [itemsSummary]);

    const getItemData = (id?: string) => menuItems.find(m => m.id === id);
    const getItemName = (id?: string) => getItemData(id)?.name || id;

    const getItemImage = useCallback((itemId: string) => {
        const item = menuItems.find(m => m.id === itemId);
        return (item as any)?.image_url || '/images/menu-items/default.jpg';
    }, [menuItems]);

    const getItemDisplayName = useCallback((itemId: string) => {
        const item = menuItems.find(m => m.id === itemId);
        return item?.name || 'Unknown Item';
    }, [menuItems]);

    const handlePageChange = (page: number) => {
        if (activeTab === 'general') {
            setGeneralPagination(prev => ({ ...prev, currentPage: page }));
        } else {
            setItemsPagination(prev => ({ ...prev, currentPage: page }));
        }
        loadFeedbacks(page);
    };

    const handleLimitChange = (limit: number) => {
        if (activeTab === 'general') {
            setGeneralPagination(prev => ({ ...prev, itemsPerPage: limit, currentPage: 1 }));
        } else {
            setItemsPagination(prev => ({ ...prev, itemsPerPage: limit, currentPage: 1 }));
        }
    };

    // Get current pagination values based on active tab
    const currentPagination = activeTab === 'general' ? generalPagination : itemsPagination;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
            <div className="max-w-7xl mx-auto p-6">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-white rounded-xl shadow-lg">
                                <img src='/icons/image.png' className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900  dark:text-white">{t('feedback.title', 'Feedback')}</h1>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">{t('feedback.subtitle', 'Customer insights and experiences')}</p>
                            </div>
                        </div>

                        {/* Tab Navigation */}
                        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl">
                            <button
                                onClick={() => setActiveTab('general')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'general'
                                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                            >
                                <FiStar className="w-4 h-4" />
                                {t('feedback.general', 'General')}
                            </button>
                            <button
                                onClick={() => setActiveTab('items')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'items'
                                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                            >
                                <FiImage className="w-4 h-4" />
                                {t('feedback.itemFeedback', 'Items')}
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-3 mb-6 ">
                        {activeTab === 'items' && (
                            <Select
                                value={selectedItemId}
                                onChange={setSelectedItemId}
                                options={[
                                    { value: 'all', label: t('feedback.filter.allItems', 'All items'), icon: <FiImage className="w-4 h-4" /> },
                                    ...menuItems.map((m) => {
                                        const arData = m.ar && typeof m.ar === 'object' && !Array.isArray(m.ar) ? m.ar as { name?: string } : null;
                                        const displayName = i18n.language === 'ar' && arData?.name ? arData.name : m.name;
                                        return {
                                            value: m.id,
                                            label: displayName,
                                            icon: <FiSearch className="w-4 h-4" />
                                        };
                                    })
                                ]}
                                placeholder={t('feedback.filter.allItems', 'All items')}
                                className="min-w-[200px]"
                            />
                        )}

                        <Select
                            value={sentiment}
                            onChange={(value) => setSentiment(value as 'all' | 'positive' | 'negative')}
                            options={[
                                { value: 'all', label: t('feedback.filter.all', 'All Sentiment'), icon: <FiMessageCircle className="w-4 h-4" /> },
                                { value: 'positive', label: `${t('feedback.filter.positive', 'Positive')}`, icon: <FiThumbsUp className="w-4 h-4 text-green-500" /> },
                                { value: 'negative', label: `${t('feedback.filter.negative', 'Negative')}`, icon: <FiThumbsDown className="w-4 h-4 text-red-500" /> }
                            ]}
                            placeholder={t('feedback.filter.all', 'All Sentiment')}
                            className="min-w-[200px]"
                        />

                        <Select
                            value={writtenBy.toString()}
                            onChange={(value) => setWrittenBy(Number(value) as 1 | -1)}
                            options={[
                                { value: '1', label: t('feedback.filter.customer'), icon: <span className="text-lg">👤</span> },
                                { value: '-1', label: t('feedback.filter.ai'), icon: <img src="/icons/Robot_Head.crop.webp" alt="AI" className="w-6 h-6 object-contain" /> }
                            ]}
                            placeholder={t('feedback.filter.customer')}
                            className="min-w-[200px]"
                        />
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-100 text-sm font-medium">{t('feedback.filter.positive', 'Positive')}</p>
                                    <p className="text-2xl font-bold">
                                        {activeTab === 'general'
                                            ? generalFeedbacks.filter(f => f.is_positive).length
                                            : itemFeedbacks.reduce((sum, item) => sum + item.feedbacks.filter(f => f.is_positive).length, 0)
                                        }
                                    </p>
                                </div>
                                <FiThumbsUp className="w-8 h-8 text-green-200" />
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-red-500 to-rose-600 rounded-2xl p-6 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-red-100 text-sm font-medium">{t('feedback.filter.negative', 'Negative')}</p>
                                    <p className="text-2xl font-bold">
                                        {activeTab === 'general'
                                            ? generalFeedbacks.filter(f => !f.is_positive && f.is_positive !== null).length
                                            : itemFeedbacks.reduce((sum, item) => sum + item.feedbacks.filter(f => !f.is_positive && f.is_positive !== null).length, 0)
                                        }
                                    </p>
                                </div>
                                <FiThumbsDown className="w-8 h-8 text-red-200" />
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-100 text-sm font-medium">{t('feedback.ratings', 'Ratings')}</p>
                                    <p className="text-2xl font-bold">
                                        {activeTab === 'general'
                                            ? generalFeedbacks.length
                                            : itemFeedbacks.reduce((sum, item) => sum + item.feedbacks.length, 0)
                                        }
                                    </p>
                                </div>
                                <FiMessageCircle className="w-8 h-8 text-blue-200" />
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 shadow-sm"
                    >
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                            {error}
                        </div>
                    </motion.div>
                )}

                {/* Content Section */}
                <AnimatePresence mode="wait">
                    {activeTab === 'general' ? (
                        <motion.div
                            key="general"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3 }}
                            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                        >
                            <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                            <FiStar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('feedback.general', 'General Feedback')}</h2>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">{t('feedback.generalDesc', 'Overall restaurant experience')}</p>
                                        </div>
                                    </div>
                                    {branchSummary?.avg_rating !== null && branchSummary?.avg_rating !== undefined && (
                                        <div className="flex items-center gap-2 bg-yellow-100 dark:bg-yellow-900/30 px-4 py-2 rounded-lg">
                                            <FiStar className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                                            <div>
                                                <p className="text-lg font-bold text-yellow-900 dark:text-yellow-100">
                                                    {(branchSummary.avg_rating * 5).toFixed(1)}/5
                                                </p>
                                                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                                    {branchSummary.total_rated_feedbacks} {t('feedback.ratings', 'ratings')}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-6">
                                {isLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                                            {t('common.loading', 'Loading...')}
                                        </div>
                                    </div>
                                ) : (
                                    <AnimatePresence>
                                        {generalFeedbacks.length === 0 ? (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="text-center py-12"
                                            >
                                                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                                    <FiMessageCircle className="w-8 h-8 text-gray-400" />
                                                </div>
                                                <p className="text-gray-500 dark:text-gray-400">{t('feedback.noGeneral', 'No general feedback')}</p>
                                            </motion.div>
                                        ) : (
                                            <div className="space-y-4">
                                                {generalFeedbacks.map((f: FeedbackItem, index) => (
                                                    <motion.div
                                                        key={f.id}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: index * 0.05 }}
                                                        className={`p-6 rounded-xl border-2 transition-all hover:shadow-md ${
                                                            f.is_positive
                                                                ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/20'
                                                                : f.is_positive === false
                                                                ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/20'
                                                                : 'bg-gray-50 dark:bg-gray-900/10 border-gray-200 dark:border-gray-800'
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-4">
                                                            <div className={`p-3 rounded-full ${
                                                                f.is_positive
                                                                    ? 'bg-green-500 text-white'
                                                                    : f.is_positive === false
                                                                    ? 'bg-red-500 text-white'
                                                                    : 'bg-gray-500 text-white'
                                                            }`}>
                                                                {f.is_positive ? <FiThumbsUp className="w-5 h-5" /> : f.is_positive === false ? <FiThumbsDown className="w-5 h-5" /> : <FiMessageCircle className="w-5 h-5" />}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-gray-800 dark:text-gray-200 font-medium leading-relaxed mb-2">{f.feedback || '-'}</p>
                                                                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                                                    <div className="flex items-center gap-2">
                                                                        <FiClock className="w-4 h-4" />
                                                                        <span>{new Date(f.created_at).toLocaleString()}</span>
                                                                    </div>
                                                                    {f.normalized_rating !== null && (
                                                                        <div className="flex items-center gap-2">
                                                                            <FiStar className="w-4 h-4 text-yellow-500" />
                                                                            <span>{(f.normalized_rating * 5).toFixed(1)}/5</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        )}
                                    </AnimatePresence>
                                )}
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="items"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3 }}
                            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                        >
                            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                        <FiImage className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('feedback.itemFeedback', 'Item Feedback')}</h2>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">{t('feedback.itemDesc', 'Feedback about specific menu items')}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6">
                                {isLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-500 border-t-transparent"></div>
                                            {t('common.loading', 'Loading...')}
                                        </div>
                                    </div>
                                ) : (
                                    <AnimatePresence>
                                        {itemFeedbacks.length === 0 ? (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="text-center py-12"
                                            >
                                                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                                    <FiImage className="w-8 h-8 text-gray-400" />
                                                </div>
                                                <p className="text-gray-500 dark:text-gray-400">{t('feedback.noItem', 'No item feedback')}</p>
                                            </motion.div>
                                        ) : (
                                            <div className="space-y-6">
                                                {itemFeedbacks.map((itemSummary: ItemSummary, index) => {
                                                    return (
                                                        <motion.div
                                                            key={itemSummary.item_id}
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ delay: index * 0.1 }}
                                                            className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-2xl p-6 border border-gray-200 dark:border-gray-600"
                                                        >
                                                            {/* Item Header */}
                                                            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200 dark:border-gray-600">
                                                                <div className="relative">
                                                                    {itemSummary.image_url ? (
                                                                        <img
                                                                            src={itemSummary.image_url}
                                                                            alt={itemSummary.name}
                                                                            className="w-16 h-16 rounded-xl object-cover shadow-lg ring-2 ring-white dark:ring-gray-700"
                                                                            onError={(e) => {
                                                                                const target = e.target as HTMLImageElement;
                                                                                target.style.display = 'none';
                                                                                target.nextElementSibling?.classList.remove('hidden');
                                                                            }}
                                                                        />
                                                                    ) : null}
                                                                    <div className={`w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-xl flex items-center justify-center shadow-lg ${itemSummary.image_url ? 'hidden' : ''}`}>
                                                                        <span className="text-white font-bold text-lg">{itemSummary.name?.charAt(0).toUpperCase() || '?'}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{itemSummary.name}</h3>
                                                                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                                                                        <span className="flex items-center gap-1">
                                                                            <FiMessageCircle className="w-4 h-4" />
                                                                            {itemSummary.feedbacks.length} {itemSummary.feedbacks.length === 1 ? t('feedback.review', 'review') : t('feedback.reviews', 'reviews')}
                                                                        </span>
                                                                        {itemSummary.avg_rating !== null && (
                                                                            <span className="flex items-center gap-1">
                                                                                <FiStar className="w-4 h-4 text-yellow-500" />
                                                                                {(itemSummary.avg_rating * 5).toFixed(1)}/5 ({itemSummary.total_rated_feedbacks} rated)
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Feedback List */}
                                                            <div className="space-y-4">
                                                                {itemSummary.feedbacks.map((f: FeedbackItem, feedbackIndex: number) => (
                                                                    <motion.div
                                                                        key={f.id}
                                                                        initial={{ opacity: 0, x: -10 }}
                                                                        animate={{ opacity: 1, x: 0 }}
                                                                        transition={{ delay: (index * 0.1) + (feedbackIndex * 0.05) }}
                                                                        className={`p-4 rounded-xl border transition-all hover:shadow-md ${
                                                                            f.is_positive
                                                                                ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700'
                                                                                : f.is_positive === false
                                                                                ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-700'
                                                                                : 'bg-gray-50 dark:bg-gray-900/10 border-gray-200 dark:border-gray-700'
                                                                        }`}
                                                                    >
                                                                        <div className="flex items-start gap-3">
                                                                            <div className={`p-2 rounded-full flex-shrink-0 ${
                                                                                f.is_positive
                                                                                    ? 'bg-green-500 text-white'
                                                                                    : f.is_positive === false
                                                                                    ? 'bg-red-500 text-white'
                                                                                    : 'bg-gray-500 text-white'
                                                                            }`}>
                                                                                {f.is_positive ? <FiThumbsUp className="w-4 h-4" /> : f.is_positive === false ? <FiThumbsDown className="w-4 h-4" /> : <FiMessageCircle className="w-4 h-4" />}
                                                                            </div>
                                                                            <div className="flex-1 min-w-0">
                                                                                <p className="text-gray-800 dark:text-gray-200 leading-relaxed mb-2">{f.feedback || '-'}</p>
                                                                                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <FiClock className="w-3 h-3" />
                                                                                        <span>{new Date(f.created_at).toLocaleString()}</span>
                                                                                    </div>
                                                                                    {f.normalized_rating !== null && (
                                                                                        <div className="flex items-center gap-2">
                                                                                            <FiStar className="w-3 h-3 text-yellow-500" />
                                                                                            <span>{(f.normalized_rating * 5).toFixed(1)}/5</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </motion.div>
                                                                ))}
                                                            </div>
                                                        </motion.div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </AnimatePresence>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Pagination */}
                {currentPagination.totalCount > currentPagination.itemsPerPage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="mt-8"
                    >
                        <PaginationWithLimitSelector
                            currentPage={currentPagination.currentPage}
                            totalPages={Math.ceil(currentPagination.totalCount / currentPagination.itemsPerPage)}
                            totalItems={currentPagination.totalCount}
                            itemsPerPage={currentPagination.itemsPerPage}
                            onPageChange={handlePageChange}
                            onLimitChange={handleLimitChange}
                            className="justify-center"
                        />
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default FeedbackPage;
